import type { AgentThreadSummary } from './protocol';

export const AGENT_THREAD_GROUP = {
  TODAY: 'TODAY',
  YESTERDAY: 'YESTERDAY',
  OLDER: 'OLDER',
} as const;

export type AgentThreadGroup = (typeof AGENT_THREAD_GROUP)[keyof typeof AGENT_THREAD_GROUP];

export type AgentRelativeAge = {
  key: 'seconds' | 'minutes' | 'hours' | 'days' | 'months' | 'years';
  value: number;
};

export type AgentThreadPresentationGroup = {
  type: AgentThreadGroup;
  threads: readonly AgentThreadSummary[];
};

export type AgentThreadPresentationOptions = {
  locale: string;
  now: Date;
  search: string;
  timezone: string;
};

const dateValue = (value: string): number | null => {
  const parsed = Date.parse(value);
  return Number.isFinite(parsed) ? parsed : null;
};

const localDayOrdinal = (date: Date, timezone: string): number => {
  const parts = new Intl.DateTimeFormat('en-US-u-ca-iso8601', {
    day: '2-digit',
    month: '2-digit',
    timeZone: timezone,
    year: 'numeric',
  }).formatToParts(date);
  const valueFor = (type: Intl.DateTimeFormatPartTypes): number =>
    Number(parts.find((part) => part.type === type)?.value);
  // Date.UTC supplies a Gregorian calendar ordinal independent of the browser's local timezone.
  return Math.floor(Date.UTC(valueFor('year'), valueFor('month') - 1, valueFor('day')) / (24 * 60 * 60 * 1000));
};

/**
 * Returns a new deterministic thread order without mutating the server-owned summary list.
 *
 * Valid update and access dates sort newest first. Malformed dates intentionally stay in Older,
 * after valid summaries, because an unknown recency must never appear as a current conversation.
 */
export function sortAgentThreads(threads: readonly AgentThreadSummary[]): AgentThreadSummary[] {
  return [...threads].sort((left, right) => {
    const leftUpdated = dateValue(left.updatedAt);
    const rightUpdated = dateValue(right.updatedAt);
    if (leftUpdated !== rightUpdated) {
      if (leftUpdated === null) {
        return 1;
      }
      if (rightUpdated === null) {
        return -1;
      }
      return rightUpdated - leftUpdated;
    }
    const leftAccessed = dateValue(left.lastAccessedAt);
    const rightAccessed = dateValue(right.lastAccessedAt);
    if (leftAccessed !== rightAccessed) {
      if (leftAccessed === null) {
        return 1;
      }
      if (rightAccessed === null) {
        return -1;
      }
      return rightAccessed - leftAccessed;
    }
    return left.externalThreadId.localeCompare(right.externalThreadId);
  });
}

/** Returns a localized relative-time descriptor suitable for the shared i18n formatter. */
export function getAgentThreadRelativeAge(updatedAt: string, now: Date): AgentRelativeAge | null {
  const updated = dateValue(updatedAt);
  if (updated === null) {
    return null;
  }
  const seconds = Math.max(0, Math.floor((now.getTime() - updated) / 1000));
  if (seconds < 60) {
    return { key: 'seconds', value: -seconds };
  }
  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) {
    return { key: 'minutes', value: -minutes };
  }
  const hours = Math.floor(minutes / 60);
  if (hours < 24) {
    return { key: 'hours', value: -hours };
  }
  const days = Math.floor(hours / 24);
  if (days < 30) {
    return { key: 'days', value: -days };
  }
  const months = Math.floor(days / 30);
  if (months < 12) {
    return { key: 'months', value: -months };
  }
  return { key: 'years', value: -Math.floor(months / 12) };
}

/**
 * Filters and groups summaries for the sole Agent history surface.
 *
 * The caller supplies the app display timezone and clock so rendering stays deterministic and a
 * travelling coordinator sees the same day boundaries as the rest of ModernLoop.
 */
export function getAgentThreadPresentationGroups(
  threads: readonly AgentThreadSummary[],
  { locale, now, search, timezone }: AgentThreadPresentationOptions
): AgentThreadPresentationGroup[] {
  const query = search.trim().toLocaleLowerCase(locale);
  const todayOrdinal = localDayOrdinal(now, timezone);
  const orderedGroups = [AGENT_THREAD_GROUP.TODAY, AGENT_THREAD_GROUP.YESTERDAY, AGENT_THREAD_GROUP.OLDER] as const;
  const groups = new Map<AgentThreadGroup, AgentThreadSummary[]>(orderedGroups.map((group) => [group, []]));
  for (const thread of sortAgentThreads(threads)) {
    if (query.length > 0 && !(thread.title ?? '').toLocaleLowerCase(locale).includes(query)) {
      continue;
    }
    const updated = dateValue(thread.updatedAt);
    const dayDelta = updated === null ? null : todayOrdinal - localDayOrdinal(new Date(updated), timezone);
    const group =
      dayDelta === 0
        ? AGENT_THREAD_GROUP.TODAY
        : dayDelta === 1
          ? AGENT_THREAD_GROUP.YESTERDAY
          : AGENT_THREAD_GROUP.OLDER;
    groups.get(group)?.push(thread);
  }
  return orderedGroups
    .filter((group) => (groups.get(group)?.length ?? 0) > 0)
    .map((type) => ({
      type,
      threads: groups.get(type) ?? [],
    }));
}
